import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";

export interface InlineComboboxItem {
  value: string;
  label: string;
  searchText?: string;
}

export interface InlineComboboxGroup {
  heading: string;
  items: InlineComboboxItem[];
}

interface InlineComboboxProps {
  value: string;
  display: string;
  onChange: (value: string, label: string) => void;
  groups: InlineComboboxGroup[];
  placeholder?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  disabled?: boolean;
}

export function InlineCombobox({
  value,
  display,
  onChange,
  groups,
  placeholder = "Select an option…",
  searchPlaceholder = "Search…",
  loading = false,
  disabled = false,
}: InlineComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (itemValue: string, itemLabel: string) => {
    onChange(itemValue, itemLabel);
    setIsOpen(false);
  };

  return (
    <div>
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between font-normal"
        disabled={disabled || loading}
        onClick={() => setIsOpen(o => !o)}
      >
        <span className="truncate">{display || (loading ? "Loading…" : placeholder)}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {isOpen && (
        <div className="rounded-md border mt-1 bg-popover text-popover-foreground shadow-sm">
          <Command>
            <CommandInput placeholder={searchPlaceholder} autoFocus />
            <CommandList className="max-h-[180px] overflow-y-auto">
              <CommandEmpty>No results found.</CommandEmpty>
              {groups.map(group => (
                <CommandGroup key={group.heading} heading={group.heading}>
                  {group.items.map(item => (
                    <CommandItem
                      key={item.value}
                      value={item.searchText ?? item.label.toLowerCase()}
                      onSelect={() => handleSelect(item.value, item.label)}
                    >
                      <Check className={`mr-2 h-4 w-4 ${value === item.value ? "opacity-100" : "opacity-0"}`} />
                      {item.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
